import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  UsersThree, MagnifyingGlass, Lightning, Buildings, FileText,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CustomersListPage() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/customers`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setCustomers(d.customers || []);
      }
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = customers.filter(c =>
    !search ||
    c.ragione_sociale?.toLowerCase().includes(search.toLowerCase()) ||
    c.partita_iva?.includes(search) ||
    c.codice_fiscale?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="p-8 text-slate-500">Caricamento clienti attivi...</div>;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="customers-list-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <UsersThree size={32} weight="duotone" className="text-orange-500" />
            Clienti Attivi
          </h1>
          <p className="text-slate-500 mt-1">
            Anagrafica clienti con contratto in attivazione o già attivo. {customers.length} clienti.
          </p>
        </div>
      </div>

      <div className="bg-white border rounded-lg p-3 mb-4 flex items-center gap-2">
        <MagnifyingGlass size={16} className="text-slate-400 ml-2" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)}
               placeholder="Cerca per ragione sociale, P.IVA o C.F..."
               className="border-0 focus-visible:ring-0" data-testid="customers-search" />
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-lg border p-12 text-center">
          <UsersThree size={64} className="mx-auto text-slate-300 mb-4" weight="duotone" />
          <p className="text-slate-500">
            {customers.length === 0
              ? 'Nessun cliente attivo. I lead diventano clienti dopo l\'attivazione del contratto in back-office.'
              : 'Nessun risultato'}
          </p>
        </div>
      ) : (
        <div className="grid gap-3">
          {filtered.map(c => (
            <div key={c.id}
                 className="bg-white rounded-lg border p-4 hover:shadow-md hover:border-orange-200 transition cursor-pointer"
                 onClick={() => navigate(`/customers/${c.id}`)}
                 data-testid={`customer-${c.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center">
                  <Buildings size={24} weight="duotone" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-lg font-semibold">{c.ragione_sociale}</h3>
                    {c.customer_status === 'in_attivazione' ? (
                      <Badge className="bg-amber-100 text-amber-800 border border-amber-300" data-testid={`status-in-attivazione-${c.id}`}>
                        ⏳ In attivazione
                      </Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-700" data-testid={`status-attivo-${c.id}`}>Attivo</Badge>
                    )}
                    {c.tags?.map(t => <Badge key={t} variant="outline">{t}</Badge>)}
                  </div>
                  <div className="text-xs text-slate-500 font-mono mt-1">
                    {c.partita_iva && `P.IVA ${c.partita_iva}`}
                    {c.codice_fiscale && ` · CF ${c.codice_fiscale}`}
                  </div>
                  <div className="flex gap-4 text-sm text-slate-600 mt-2">
                    <span className="flex items-center gap-1">
                      <Lightning size={14} className="text-yellow-500" /> {c.pod_count} POD
                    </span>
                    <span className="flex items-center gap-1">
                      <FileText size={14} className="text-blue-500" /> {c.contracts_attivi} contratti attivi
                    </span>
                  </div>
                </div>
                <Button size="sm" variant="outline" data-testid={`open-customer-${c.id}`}>
                  Apri scheda →
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
